import { useState, useEffect } from 'react'
import { CheckCircle, XCircle, HelpCircle } from 'lucide-react'

interface PassStat {
  pass_id: string
  total_runs: number
  avg_agreement: number | null
  high_agreement_count: number
  validated_count: number
}

const PASS_LABELS: Record<string, string> = {
  EVIDENCE_GROUNDED: 'Evidence Grounded',
  SEGMENT_RESPONSE: 'Segment Response',
  SKEPTICAL_RESEARCHER: 'Skeptical Researcher',
  BEHAVIORAL_DRIVER: 'Behavioral Driver',
  COUNTERFACTUAL_RISK: 'Counterfactual Risk',
  QUANTITATIVE_DIST: 'Quantitative Distribution',
  VALIDATION_PLANNER: 'Validation Planner',
}

export function CalibrationLedgerPage() {
  const [stats, setStats] = useState<PassStat[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('echo_session')
    if (!token) {
      setLoading(false)
      return
    }
    fetch('/api/v1/analytics/calibration-ledger', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then((data: unknown) => {
        const d = data as { pass_stats?: PassStat[] } | null
        if (d?.pass_stats) setStats(d.pass_stats)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-6 max-w-3xl p-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Calibration Ledger</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Per-pass prediction accuracy across all validated runs</p>
      </div>

      {loading ? (
        <div className="space-y-2 animate-pulse">
          <div className="h-8 bg-soft-panel rounded-xl w-full" />
          <div className="h-8 bg-soft-panel rounded-xl w-full" />
          <div className="h-8 bg-soft-panel rounded-xl w-full" />
        </div>
      ) : stats.length === 0 ? (
        <p className="text-sm text-slate-400 py-4">No calibration runs recorded yet.</p>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border bg-surface-subtle/30">
            <p className="text-xs text-muted-foreground">Agreement score ≥ 80% = high agreement with human panel outcome</p>
          </div>
          <div className="divide-y divide-border">
            {stats.map(stat => {
              const pct = stat.avg_agreement != null ? Math.round(stat.avg_agreement * 100) : null
              const highPct = stat.validated_count > 0
                ? Math.round((stat.high_agreement_count / stat.validated_count) * 100)
                : null

              return (
                <div key={stat.pass_id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {pct == null ? (
                        <HelpCircle size={14} className="text-muted-foreground shrink-0" />
                      ) : pct >= 80 ? (
                        <CheckCircle size={14} className="text-green-600 shrink-0" />
                      ) : (
                        <XCircle size={14} className="text-orange-500 shrink-0" />
                      )}
                      <span className="text-sm font-medium text-foreground">
                        {PASS_LABELS[stat.pass_id] ?? stat.pass_id}
                      </span>
                    </div>
                    <div className="flex items-center gap-6 text-right">
                      <div>
                        <p className="text-xs text-muted-foreground">Avg agreement</p>
                        <p className="text-sm font-semibold text-foreground">{pct != null ? `${pct}%` : 'No data'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">High-conf</p>
                        <p className="text-sm font-medium text-foreground">
                          {highPct != null ? `${highPct}%` : '—'} <span className="text-xs text-muted-foreground">({stat.high_agreement_count}/{stat.validated_count})</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Runs</p>
                        <p className="text-sm font-medium text-foreground">{stat.total_runs}</p>
                      </div>
                    </div>
                  </div>
                  {pct != null && (
                    <div className="h-1.5 rounded-full bg-border overflow-hidden mt-1">
                      <div
                        className={`h-full rounded-full ${pct >= 80 ? 'bg-green-500' : pct >= 65 ? 'bg-echo-indigo' : 'bg-orange-400'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Scores accumulate as validation runs complete. Higher agreement means the pass consistently predicts what real humans confirm.
        The Skeptical Researcher pass intentionally dissents — a lower agreement score there is expected and healthy.
      </p>
    </div>
  )
}

export default CalibrationLedgerPage
